import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Button, IconButton, CircularProgress, Menu, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Snackbar, Alert, alpha, Tooltip,
} from '@mui/material';
import {
  CreateNewFolder as NewFolderIcon, CloudUpload as UploadIcon, Folder as FolderIcon,
  MoreHoriz as MoreIcon, Home as HomeIcon, DeleteOutline as DeleteIcon, Edit as EditIcon,
  ChevronRight as ChevronIcon, Image as ImageIcon,
} from '@mui/icons-material';
import axios, { API, getToken } from '../api';
import { designTokens as t } from '../theme';

const ACCENT = '#f43f5e';
const QUOTA = 500;

export default function Gallery() {
  const [folders, setFolders] = useState([]);
  const [totalFiles, setTotalFiles] = useState(0);
  const [current, setCurrent] = useState(null); // null = racine, sinon {id, name}
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [folderMenu, setFolderMenu] = useState({ anchor: null, folder: null });
  const [newFolderDlg, setNewFolderDlg] = useState({ open: false, name: '' });
  const [renameDlg, setRenameDlg] = useState({ open: false, folder: null, name: '' });
  const [uploading, setUploading] = useState(false);
  const [dragFolder, setDragFolder] = useState(null); // id du dossier survolé en drag
  const [toast, setToast] = useState({ open: false, msg: '', sev: 'success' });
  const fileInput = useRef(null);
  const notify = (msg, sev = 'success') => setToast({ open: true, msg, sev });

  const fetchFolders = useCallback(async () => {
    const { data } = await axios.get(`${API}/api/gallery/folders`);
    setFolders(data.folders || []);
    setTotalFiles(data.total_files || 0);
  }, []);

  const fetchFiles = useCallback(async (folderId) => {
    const { data } = await axios.get(`${API}/api/gallery/files`, { params: { folder: folderId } });
    setFiles(data.files || []);
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      await fetchFolders();
      await fetchFiles(current ? current.id : 0);
    } catch (e) {
      notify(e.response?.data?.error || 'Erreur de chargement', 'error');
    } finally {
      setLoading(false);
    }
  }, [fetchFolders, fetchFiles, current]);

  useEffect(() => { refresh(); }, [refresh]);

  const openFolder = (f) => setCurrent(f);
  const goRoot = () => setCurrent(null);

  const createFolder = async () => {
    const name = newFolderDlg.name.trim();
    if (!name) return;
    try {
      await axios.post(`${API}/api/gallery/folders`, { name });
      setNewFolderDlg({ open: false, name: '' });
      fetchFolders();
      notify('Dossier créé');
    } catch (e) { notify(e.response?.data?.error || 'Erreur', 'error'); }
  };

  const renameFolder = async () => {
    const name = renameDlg.name.trim();
    if (!name || !renameDlg.folder) return;
    try {
      await axios.put(`${API}/api/gallery/folders/${renameDlg.folder.id}`, { name });
      setRenameDlg({ open: false, folder: null, name: '' });
      if (current && current.id === renameDlg.folder.id) setCurrent({ ...current, name });
      fetchFolders();
      notify('Dossier renommé');
    } catch (e) { notify(e.response?.data?.error || 'Erreur', 'error'); }
  };

  const deleteFolder = async (f) => {
    if (!window.confirm(`Supprimer le dossier "${f.name}" et tous ses fichiers ?`)) return;
    try {
      await axios.delete(`${API}/api/gallery/folders/${f.id}`);
      if (current && current.id === f.id) setCurrent(null);
      refresh();
      notify('Dossier supprimé');
    } catch (e) { notify(e.response?.data?.error || 'Erreur', 'error'); }
  };

  const moveFile = async (fileId, folderId) => {
    try {
      await axios.put(`${API}/api/gallery/files/${fileId}/move`, { folder_id: folderId });
      refresh();
      notify('Fichier déplacé');
    } catch (e) { notify(e.response?.data?.error || 'Erreur de déplacement', 'error'); }
  };

  const deleteFile = async (id) => {
    try {
      await axios.delete(`${API}/api/gallery/files/${id}`);
      setFiles((p) => p.filter((f) => f.id !== id));
      fetchFolders();
    } catch (e) { notify('Erreur suppression', 'error'); }
  };

  const handleFilesSelected = async (e) => {
    const picked = [...(e.target.files || [])];
    if (!picked.length) return;
    const fd = new FormData();
    fd.append('folder', current ? current.id : 0);
    picked.forEach((f) => fd.append('files', f));
    setUploading(true);
    try {
      const { data } = await axios.post(`${API}/api/gallery/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      notify(`${data.added} fichier(s) ajouté(s)${data.failed?.length ? ` · ${data.failed.length} échec(s)` : ''}`, data.failed?.length ? 'warning' : 'success');
      refresh();
    } catch (err) { notify(err.response?.data?.error || 'Erreur upload', 'error'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const rawUrl = (id) => `${API}/api/gallery/files/${id}/raw?token=${getToken()}`;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <input ref={fileInput} type="file" accept="image/*,video/*" hidden multiple onChange={handleFilesSelected} />

      {/* header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
        <Box>
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.08em', color: t.mutedFaint, textTransform: 'uppercase' }}>Media Library</Typography>
          <Typography sx={{ fontSize: '1.9rem', fontWeight: 800, letterSpacing: '-0.02em' }}>Gallery</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
            <ImageIcon sx={{ fontSize: 14, color: t.mutedFaint }} />
            <Typography sx={{ fontSize: '0.82rem', color: t.muted }}>
              {current ? `${files.length} fichier(s)` : `${totalFiles} fichier(s)`} · {totalFiles}/{QUOTA} utilisés
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<NewFolderIcon />} onClick={() => setNewFolderDlg({ open: true, name: '' })}
            sx={{ borderColor: t.border, color: t.foreground }}>Nouveau dossier</Button>
          <Button variant="contained" startIcon={uploading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <UploadIcon />}
            onClick={() => fileInput.current && fileInput.current.click()} disabled={uploading}
            sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#e11d48' } }}>Upload</Button>
        </Box>
      </Box>

      {/* breadcrumb */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, border: `1px solid ${t.border}`, borderRadius: 2, bgcolor: t.card, px: 2, py: 1.25, mb: 2.5 }}>
        <Box role="button" onClick={goRoot}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); const fid = e.dataTransfer.getData('fileId'); if (fid) moveFile(Number(fid), 0); }}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', color: current ? t.muted : t.foreground, '&:hover': { color: t.foreground } }}>
          <HomeIcon sx={{ fontSize: 16 }} />
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Gallery</Typography>
        </Box>
        {current && (
          <>
            <ChevronIcon sx={{ fontSize: 16, color: t.mutedFaint }} />
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>{current.name}</Typography>
          </>
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : (
        <>
          {/* folders (uniquement à la racine) */}
          {!current && (
            <Box sx={{ mb: 3 }}>
              <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.05em', color: t.mutedFaint, textTransform: 'uppercase', mb: 1.5 }}>
                Dossiers ({folders.length})
              </Typography>
              {folders.length === 0 ? (
                <Typography sx={{ fontSize: '0.85rem', color: t.mutedFaint }}>Aucun dossier. Crée-en un avec « Nouveau dossier ».</Typography>
              ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 2 }}>
                  {folders.map((f) => (
                    <Box key={f.id} onClick={() => openFolder(f)}
                      onDragOver={(e) => { e.preventDefault(); setDragFolder(f.id); }}
                      onDragLeave={() => setDragFolder((d) => (d === f.id ? null : d))}
                      onDrop={(e) => { e.preventDefault(); setDragFolder(null); const fid = e.dataTransfer.getData('fileId'); if (fid) moveFile(Number(fid), f.id); }}
                      sx={{ position: 'relative', border: `1px solid ${dragFolder === f.id ? ACCENT : t.border}`, borderRadius: 2.5, bgcolor: dragFolder === f.id ? alpha(ACCENT, 0.08) : t.card, p: 2, cursor: 'pointer',
                        transition: 'border-color .15s', '&:hover': { borderColor: t.borderStrong } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ width: 46, height: 46, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: ACCENT }}>
                          <FolderIcon sx={{ color: '#fff', fontSize: 26 }} />
                        </Box>
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); setFolderMenu({ anchor: e.currentTarget, folder: f }); }}>
                          <MoreIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Box>
                      <Typography noWrap sx={{ fontSize: '0.92rem', fontWeight: 700, mt: 1.5 }}>{f.name}</Typography>
                      <Typography sx={{ fontSize: '0.78rem', color: t.mutedFaint }}>{f.file_count} fichier(s)</Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}

          {/* files */}
          <Box>
            <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.05em', color: t.mutedFaint, textTransform: 'uppercase', mb: 1.5 }}>
              Fichiers ({files.length})
            </Typography>
            {files.length === 0 ? (
              <Box sx={{ border: `1px dashed ${t.border}`, borderRadius: 2.5, py: 6, textAlign: 'center' }}>
                <Typography sx={{ fontSize: '0.88rem', color: t.mutedFaint, mb: 2 }}>
                  {current ? 'Ce dossier est vide.' : 'Aucun fichier à la racine.'}
                </Typography>
                <Button variant="outlined" startIcon={<UploadIcon />} onClick={() => fileInput.current && fileInput.current.click()}
                  sx={{ borderColor: t.border, color: t.foreground }}>Upload des fichiers</Button>
              </Box>
            ) : (
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 1.5 }}>
                {files.map((f) => {
                  const isVideo = (f.mime || '').startsWith('video');
                  return (
                    <Box key={f.id}
                      draggable
                      onDragStart={(e) => e.dataTransfer.setData('fileId', String(f.id))}
                      sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden', bgcolor: t.input, aspectRatio: '1 / 1', cursor: 'grab',
                      border: `1px solid ${t.border}`, '&:hover .galDel': { opacity: 1 } }}>
                      {isVideo ? (
                        <video src={rawUrl(f.id)} muted style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <img src={rawUrl(f.id)} alt={f.original_name} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      )}
                      <Box sx={{ position: 'absolute', top: 6, right: 6, px: 0.6, py: 0.15, borderRadius: 0.75, bgcolor: alpha('#000', 0.6), fontSize: '0.6rem', fontWeight: 700, color: '#fff' }}>
                        {isVideo ? 'VID' : 'IMG'}
                      </Box>
                      <Tooltip title="Supprimer">
                        <IconButton className="galDel" size="small" onClick={() => deleteFile(f.id)}
                          sx={{ position: 'absolute', bottom: 6, right: 6, opacity: 0, transition: 'opacity .15s', bgcolor: alpha('#000', 0.6), color: '#fff', '&:hover': { bgcolor: ACCENT } }}>
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        </>
      )}

      {/* folder ... menu */}
      <Menu anchorEl={folderMenu.anchor} open={Boolean(folderMenu.anchor)} onClose={() => setFolderMenu({ anchor: null, folder: null })}>
        <MenuItem onClick={() => { const f = folderMenu.folder; setFolderMenu({ anchor: null, folder: null }); openFolder(f); }}>
          <FolderIcon sx={{ fontSize: 18, mr: 1, color: t.muted }} /> Ouvrir
        </MenuItem>
        <MenuItem onClick={() => { const f = folderMenu.folder; setFolderMenu({ anchor: null, folder: null }); setRenameDlg({ open: true, folder: f, name: f.name }); }}>
          <EditIcon sx={{ fontSize: 18, mr: 1, color: t.muted }} /> Renommer
        </MenuItem>
        <MenuItem onClick={() => { const f = folderMenu.folder; setFolderMenu({ anchor: null, folder: null }); deleteFolder(f); }} sx={{ color: ACCENT }}>
          <DeleteIcon sx={{ fontSize: 18, mr: 1 }} /> Supprimer
        </MenuItem>
      </Menu>

      {/* new folder */}
      <Dialog open={newFolderDlg.open} onClose={() => setNewFolderDlg({ open: false, name: '' })} maxWidth="xs" fullWidth>
        <DialogTitle>Nouveau dossier</DialogTitle>
        <DialogContent>
          <TextField autoFocus margin="dense" label="Nom du dossier" fullWidth value={newFolderDlg.name}
            onChange={(e) => setNewFolderDlg((p) => ({ ...p, name: e.target.value }))}
            onKeyDown={(e) => { if (e.key === 'Enter') createFolder(); }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewFolderDlg({ open: false, name: '' })}>Annuler</Button>
          <Button variant="contained" onClick={createFolder} sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#e11d48' } }}>Créer</Button>
        </DialogActions>
      </Dialog>

      {/* rename */}
      <Dialog open={renameDlg.open} onClose={() => setRenameDlg({ open: false, folder: null, name: '' })} maxWidth="xs" fullWidth>
        <DialogTitle>Renommer le dossier</DialogTitle>
        <DialogContent>
          <TextField autoFocus margin="dense" label="Nom" fullWidth value={renameDlg.name}
            onChange={(e) => setRenameDlg((p) => ({ ...p, name: e.target.value }))}
            onKeyDown={(e) => { if (e.key === 'Enter') renameFolder(); }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameDlg({ open: false, folder: null, name: '' })}>Annuler</Button>
          <Button variant="contained" onClick={renameFolder}>Renommer</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={toast.open} autoHideDuration={3500} onClose={() => setToast((p) => ({ ...p, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={toast.sev} onClose={() => setToast((p) => ({ ...p, open: false }))}>{toast.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
